/**
 * A list of manifest types provided by the StUS.
 *
 * @remarks
 * The StUS is a service at RTL which returns the streams for every platform.
 *
 * Because the StUS is an integral part, and the types are the same across players,
 * we use these types to differentiate between the different manifests.
 *
 * We do not provide additional types for `dashfree` and `hlsfairplayfree`,
 * because they are for all intents and purposes the same as `dashsd` and `hlsfairplaysd`.
 */
export const ManifestType = {
  /**
   * A DASH manifest with HD quality tracks included.
   *
   * @remarks
   * This manifest is used for all platforms which do not support HLS natively.
   *
   * The HD manifest is always tried first if specified.
   * If the HD manifest fails, the SD manifest is tried instead if specified.
   */
  DashHD: 'dashhd',

  /**
   * A DASH manifest with only SD quality tracks included.
   *
   * @remarks
   * This manifest is used for all platforms which do not support HLS natively.
   *
   * The SD manifest is only tried if the HD manifest fails or is not specified.
   *
   * The main difference between the SD and HD manifest is the amount of tracks included,
   * where the SD tracks are lower quality.
   *
   * Because the tracks are lower quality, the SD manifest may use a different security level,
   * and thus may be playable platforms that cannot play the HD manifest.
   */
  DashSD: 'dashsd',

  /**
   * An HLS manifest with HD quality tracks included.
   *
   * @remarks
   * This manifest is used for all platforms which support HLS natively, which are:
   * - iOS devices
   * - macOS Safari
   *
   * The HD manifest is always tried first if specified.
   * If the HD manifest fails, the SD manifest is tried instead if specified.
   */
  HlsHD: 'hlsfairplayhd',

  /**
   * An HLS manifest with only SD quality tracks included.
   *
   * @remarks
   * This manifest is used for all platforms which support HLS natively, which are:
   * - iOS devices
   * - macOS Safari
   *
   * The SD manifest is only tried if the HD manifest fails or is not specified.
   *
   * DISCLAIMER:
   * The manifests returned by the StUS are identical for both SD and HD.
   * This means that the SD manifest will contain the same tracks as the HD manifest,
   * and will most likely fail as well if the HD manifest fails, barring some errors like network issues.
   */
  HlsSD: 'hlsfairplaysd',

  /**
   * A progressive MP4 video.
   *
   * @remarks
   * This type is currently not returned by the StUS.
   *
   * We use this preset for progressive streams, which are not encrypted e.g. radio streams and podcasts.
   *
   * This type has the lowest priority and will only be tried if all other manifests fail or are not specified.
   */
  Progressive: 'progressive',
} as const;

/**
 * {@inheritDoc ManifestType:var}
 */
export type ManifestType = (typeof ManifestType)[keyof typeof ManifestType];
